import { useExtraRate } from '@battleops/client/src/data/useProfit';
import { commitWrites } from '@battleops/client/src/data/commit';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { tagPath } from '../lib/tagLink';
import { collection, doc, onSnapshot, query, where, setDoc, deleteDoc, newId } from '@battleops/localstore';
import { db } from '@battleops/client/src/data/store';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import { useSession } from '@battleops/client/src/context/SessionContext';
import { useDepartments } from '@battleops/client/src/context/DepartmentContext';
import { useNotes } from '@battleops/client/src/context/NoteContext';
import { localToday, localDateStr, formatDurationShort, computeTotalTaskTime, isProjectLive, taskDepartmentResolver, notesForCard, GENERAL_DEPARTMENT_ID, tagVocab, suggestTags, withTag, commands, moneyShort, SCOPES, SCOPE_LABEL, SCOPE_HINT, effectiveScope, needsScope, planStartFor, scopePatch, isDepartmentLocked, projectsForDepartmentMove, isVoidTask } from '@battleops/shared';
import { useTagPicker } from '@battleops/client/src/data/useTags';
import Icon from './Icon';
import TaskList from './TaskList';
import TaskGoalStrip from './dept/TaskGoalStrip';
import EarnedLine from './profile/EarnedLine';
import { useTaskGoal } from '@battleops/client/src/data/useDeptGoals';
import { useMakeTaskGoal } from '../lib/taskGoal';
import { useStartTask, dayLabel, hhmm } from '../lib/actions';
import { useNow, useTaskModal } from '../hooks';
import s from './TaskDetail.module.css';
import { confirm, choose } from './Dialog';

export default function TaskDetail({ id, onClose }) {
  const { tasks, getChildren, updateTask, completeTask, resetTask, addTask, addNote, deleteTask, deleteTaskTree,
    addTimeEntry, deleteTimeEntry, activeTaskId, timer, setTaskDone, allClockSessions, clockSessions, tasksWithVoid, restoreTask } = useTasks();
  const { projects } = useProjects();
  const { departments, departmentMode } = useDepartments();
  const { identity } = useSession();
  const navigate = useNavigate();
  const start = useStartTask();
  const details = useTaskModal();
  const now = useNow(activeTaskId === id ? 1000 : 60000);
  const task = tasksWithVoid.find((t) => t.id === id);   // a void task (closed with its project) opens too — VOID + Restore
  const tg = useTaskGoal(task);              // the goal on this task (boards B7)
  const makeGoal = useMakeTaskGoal();
  const today = localToday();
  const [title, setTitle] = useState(task?.title || '');
  const [desc, setDesc] = useState(task?.description || '');
  const [comments, setComments] = useState([]);
  useEffect(() => { setTitle(task?.title || ''); setDesc(task?.description || ''); }, [task?.title, task?.description]);
  useEffect(() => onSnapshot(query(collection(db, 'comments'), where('taskId', '==', id)), (snap) => {
    setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.at || 0) - (b.at || 0)));
  }), [id]);

  const worked = useMemo(() => {
    const byDay = new Map();
    for (const e of task?.timeEntries || []) { if (!byDay.has(e.date)) byDay.set(e.date, []); byDay.get(e.date).push(e); }
    return [...byDay.entries()].sort((a, b) => String(b[0]).localeCompare(String(a[0])))
      .map(([date, list]) => ({ date, list: list.sort((a, b) => String(a.startTime).localeCompare(String(b.startTime))), ms: list.reduce((m, e) => m + (e.duration || 0), 0) }));
  }, [task?.timeEntries]);
  const timeline = useMemo(() => [
    ...(task?.noteEvents || []).map((n, i) => ({ key: `q${i}`, at: n.at || 0, text: n.text })),
    ...(!(task?.noteEvents || []).length ? (task?.notes || []).map((t, i) => ({ key: `l${i}`, at: 0, text: t })) : []),
  ].sort((a, b) => (b.at || 0) - (a.at || 0)), [task?.noteEvents, task?.notes]);

  if (!task) return <div className={s.root}><Head onClose={onClose} /><div className="empty">This task is gone (deleted, or not synced to this browser yet).</div></div>;

  const project = projects.find((p) => p.id === task.projectId);
  const color = project?.color || 'var(--c-accent)';
  const subtasks = getChildren(task.id);
  const running = activeTaskId === task.id;
  const spent = running && timer?.taskId === task.id ? computeTotalTaskTime(task, timer, now) : task.timeSpent || 0;
  const est = (task.estimatedMinutes || 0) * 60000;
  const done = task.status === 'done';
  const dept = departmentMode ? departments.find((x) => x.id === taskDepartmentResolver(tasks, departments, projects)(task)) : null;
  const deptLocked = isDepartmentLocked(project);   // the project sets it; changing it = moving the task to another project
  const guessLow = !deptLocked && task.departmentGuess?.confidence === 'low';   // inferred on capture (v0.1.242); a pick clears it
  const pickDept = async (id) => {
    const value = id === GENERAL_DEPARTMENT_ID ? null : id;
    if (!deptLocked) { updateTask({ id: task.id, departmentId: value }); return; }
    const to = departments.find((d) => d.id === id);
    const options = projectsForDepartmentMove(projects, id, departments, project.id).slice(0, 8).map((p) => ({ label: `${p.icon || ''} ${p.name}`.trim(), value: p.id }));
    const pid = await choose({ title: `${project.name} is ${dept?.name || 'General'} only`, message: `Its tasks take the project’s department. Move “${task.title}” to a project for ${to?.name || 'that department'}?`, options });
    if (pid) updateTask({ id: task.id, projectId: pid, sectionId: null, departmentId: value });
  };
  const saveTitle = () => { const t = title.trim(); if (t && t !== task.title) updateTask({ id: task.id, title: t }); else setTitle(task.title); };
  const saveDesc = () => { if ((desc || '') !== (task.description || '')) updateTask({ id: task.id, description: desc || null }); };

  return (
    <div className={s.root} style={{ '--tc': color }}>
      <Head onClose={onClose} project={project} onProject={() => project && navigate(`/projects/${project.id}`)} onExpand={() => details.open(task.id)} />
      <textarea className={s.title} value={title} rows={1} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} aria-label="Title" />
      {isVoidTask(task) ? (
        <div className="row" style={{ gap: 8, fontSize: 12.5, margin: '2px 0 6px' }}>
          <span className="chip" style={{ fontWeight: 800, letterSpacing: 1.2 }}>VOID</span>
          <span className="faint">Voided with {projects.find((p) => p.id === task.voidedWith)?.name || 'its project'}{task.voidedAt ? ` · ${new Date(task.voidedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : ''} — not done, nothing paid. Time on it stays.</span>
        </div>
      ) : null}
      {isVoidTask(task) ? <div className={s.actions}><button className="btn sm" onClick={() => restoreTask(task.id)}><Icon name="rotate-ccw" size={13} />Restore</button></div> : null}
      <div className={s.actions} style={isVoidTask(task) ? { display: 'none' } : undefined}>
        {!done ? (
          running
            ? <span className="chip on"><Icon name="play" size={12} />Running · <span className="num">{formatDurationShort(spent)}</span></span>
            : <button className="btn primary sm" onClick={() => start(task)}><Icon name="play" size={13} />Start</button>
        ) : null}
        <button className="btn sm" onClick={() => (done ? resetTask(task.id) : (running ? completeTask(task.id) : setTaskDone(task.id, true)))}>
          <Icon name={done ? 'rotate-ccw' : 'check'} size={13} />{done ? 'Reopen' : 'Done'}
        </button>
        {!tg.goal || tg.goal.closed ? <button className="btn sm" onClick={() => makeGoal(task)} title="Count its hours, subtasks, progress, review time or a finish date in the department score"><Icon name="crosshair" size={13} />Make this a goal</button> : null}
        <span className="spacer" />
        <button className="btn ghost sm icon danger" title="Delete task" onClick={async () => {
          if (!await confirm({ title: `Delete “${task.title}”?`, message: subtasks.length ? `Its ${subtasks.length} subtask${subtasks.length === 1 ? '' : 's'} go with it.` : undefined, danger: true })) return;
          if (subtasks.length) deleteTaskTree(task.id); else deleteTask(task.id);
          onClose();
        }}><Icon name="trash-2" size={14} /></button>
      </div>

      {tg.goal ? (
        <TaskGoalStrip goal={tg.goal} dept={tg.dept}
          onOpen={() => (tg.goal.closed ? null : navigate(`/department/${tg.dept.id}?goal=${encodeURIComponent(tg.goal.id)}`))}
          onEdit={() => (tg.goal.closed ? makeGoal(task) : navigate(`/department/${tg.dept.id}?goal=${encodeURIComponent(tg.goal.id)}`))}
          onBoard={subtasks.length ? () => navigate(`/board/task/${task.id}`) : null}
          boardLabel={`Subtask board · ${subtasks.filter((x) => x.status === 'done').length}/${subtasks.length}`} />
      ) : null}

      {/* What this task is worth to YOU on a fixed job (people.md P7) — your share only, live with your timer */}
      <EarnedLine task={task} />

      {departmentMode ? (
        <Section title="Department">
          {task.parentId ? (
            <div className="row"><span className="dot" style={{ background: dept?.color }} />{dept?.icon} {dept?.name}<span className="faint" style={{ fontSize: 12 }}>· follows its parent task</span></div>
          ) : (
            <div className="row">
              <select className="input" style={{ maxWidth: 260, borderColor: guessLow ? 'var(--c-border-strong)' : dept?.color, borderStyle: guessLow ? 'dashed' : undefined, color: guessLow ? 'var(--c-text-secondary)' : undefined }}
                value={dept?.id || GENERAL_DEPARTMENT_ID} aria-label="Department"
                onChange={(e) => pickDept(e.target.value)}>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.icon} {d.name}</option>)}
              </select>
              {!deptLocked && task.departmentGuess ? <span className="faint" style={{ fontSize: 12 }} title="Inferred when the task was added — pick one to confirm or correct it (it learns from your picks)">{guessLow ? <b style={{ letterSpacing: '0.1em', fontSize: 10.5 }}>GUESS · </b> : null}{task.departmentGuess.why}</span> : null}
              {deptLocked ? <span className="faint" style={{ fontSize: 12 }} title="Locked in the project’s settings — pick another department to move the task to another project"><Icon name="lock" size={11} /> set by the project</span> : null}
            </div>
          )}
        </Section>
      ) : null}

      {project && !task.bucket ? (
        <ScopeSection task={task} tasks={tasks} project={project} planAt={planStartFor(project, allClockSessions || clockSessions || [], tasks)}
          onPick={(scope) => updateTask({ id: task.id, ...scopePatch(scope) })} onClientOk={(clientOk) => updateTask({ id: task.id, clientOk })} />
      ) : null}

      <Section title="When">
        <div className={s.fields}>
          <label><span>Scheduled</span>
            <input className="input" type="date" value={task.scheduledDate || ''} onChange={(e) => updateTask({ id: task.id, scheduledDate: e.target.value || null })} />
          </label>
          <label><span>Time</span>
            <input className="input" type="time" value={task.scheduledTime || ''} onChange={(e) => updateTask({ id: task.id, scheduledTime: e.target.value || null })} />
          </label>
          <label><span>Estimate (min)</span>
            <input className="input num" type="number" min="0" step="5" defaultValue={task.estimatedMinutes || ''} key={task.estimatedMinutes}
              onBlur={(e) => { const v = Number(e.target.value) || null; if (v !== (task.estimatedMinutes || null)) updateTask({ id: task.id, estimatedMinutes: v }); }} />
          </label>
          <label><span>Project</span>
            <select className="input" value={task.projectId || ''} onChange={(e) => updateTask({ id: task.id, projectId: e.target.value || null, sectionId: null })}>
              <option value="">No project</option>
              {projects.filter((p) => isProjectLive(p) || p.id === task.projectId).map((p) => <option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}
            </select>
          </label>
        </div>
        <div className={s.meta}>
          <span>{dayLabel(task.scheduledDate, today)}</span>
          {task.createdAt ? <span>Created {dayLabel(localDateStr(task.createdAt), today)}</span> : null}
          {task.completedAt ? <span style={{ color: 'var(--c-success)' }}>Done {hhmm(task.completedAt)}{task.completion?.dayIndex ? ` · #${task.completion.dayIndex} that day` : ''}</span> : null}
          {task.skippedCount ? <span>Skipped ×{task.skippedCount}</span> : null}
        </div>
        {est ? (
          <div className={s.budget}>
            <div className={s.track}><div style={{ width: `${Math.min(100, (spent / est) * 100)}%`, background: spent > est ? 'var(--c-danger)' : color }} /></div>
            <span className="num" style={spent > est ? { color: 'var(--c-danger)' } : null}>{formatDurationShort(spent)} of {formatDurationShort(est)}</span>
          </div>
        ) : null}
      </Section>

      <Section title="Description">
        <textarea className="input" value={desc} placeholder="Details, specs, anything the next person needs…" onChange={(e) => setDesc(e.target.value)} onBlur={saveDesc} rows={3} />
        <TagsRow tags={task.tags || []} onChange={(tags) => updateTask({ id: task.id, tags })} />
      </Section>

      <Section title="List">
        <TaskList task={task} departmentId={task.departmentId || project?.departmentId || null} />
      </Section>

      <Section title={`Subtasks${subtasks.length ? ` · ${subtasks.filter((x) => x.status === 'done').length}/${subtasks.length}` : ''}`}>
        {subtasks.map((st) => (
          <div key={st.id} className={s.sub}>
            <button className={s.check} data-done={st.status === 'done' || undefined} onClick={() => setTaskDone(st.id, st.status !== 'done')} aria-label={st.status === 'done' ? 'Mark not done' : 'Mark done'}>
              {st.status === 'done' ? <Icon name="check" size={12} /> : null}
            </button>
            <InlineText value={st.title} done={st.status === 'done'} onSave={(t) => updateTask({ id: st.id, title: t })} />
            <button className="btn ghost sm icon" title="Delete subtask" onClick={() => deleteTask(st.id)}><Icon name="x" size={13} /></button>
          </div>
        ))}
        <AddLine placeholder="Add a subtask…" onAdd={(t) => addTask({ title: t, projectId: task.projectId || null, sectionId: task.sectionId || null, parentId: task.id, scheduledDate: task.scheduledDate || null })} />
        {subtasks.length ? <button className="btn sm" style={{ marginTop: 8 }} onClick={() => navigate(`/board/task/${task.id}`)}><Icon name="columns" size={13} />Subtask board</button> : null}
      </Section>

      <LinkedNotes task={task} />

      <Section title={`Notes${timeline.length ? ` · ${timeline.length}` : ''}`}>
        <AddLine placeholder="Add a note…" icon="edit-3" onAdd={(t) => addNote(task.id, t)} />
        {timeline.map((n) => (
          <div key={n.key} className={s.note}>
            <div>{n.text}</div>
            {n.at ? <div className="faint" style={{ fontSize: 11 }}>{dayLabel(localDateStr(n.at), today)} · {hhmm(n.at)}</div> : null}
          </div>
        ))}
      </Section>

      <Section title={`Worked · ${formatDurationShort(task.timeSpent || 0)}`}>
        {worked.map((d) => (
          <div key={d.date} className={s.day}>
            <div className={s.dayHead}><span>{dayLabel(d.date, today)}</span><span className="num">{formatDurationShort(d.ms)}</span></div>
            {d.list.map((e) => (
              <div key={e.id} className={s.entry}>
                <span className="num">{e.startTime}–{e.endTime}</span>
                <span className="faint" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.note || ''}</span>
                <span className="num">{formatDurationShort(e.duration || 0)}</span>
                <button className="btn ghost sm icon" title="Delete entry" onClick={() => confirm({ title: 'Delete this time entry?', message: 'The hours come off the task and the job cost.', danger: true }).then((ok) => ok && deleteTimeEntry(task.id, e.id))}><Icon name="x" size={12} /></button>
              </div>
            ))}
          </div>
        ))}
        <EntryForm today={today} onAdd={(entry) => addTimeEntry(task.id, entry)} />
      </Section>

      <Section title={`Comments${comments.length ? ` · ${comments.length}` : ''}`}>
        {comments.map((c) => (
          <div key={c.id} className={s.comment}>
            <div className={s.av}>{String(c.by?.name || '?')[0].toUpperCase()}</div>
            <div style={{ flex: 1 }}>
              <div className={s.cHead}>{c.by?.name || 'Someone'} <span className="faint">· {dayLabel(localDateStr(c.at), today)} {hhmm(c.at)}</span></div>
              <div>{c.text}</div>
            </div>
            {c.by?.uid === identity?.uid ? <button className="btn ghost sm icon" title="Delete comment" onClick={() => deleteDoc(doc(db, 'comments', c.id))}><Icon name="x" size={12} /></button> : null}
          </div>
        ))}
        <AddLine placeholder="Comment…" icon="message-square" onAdd={(t) => {
          const cid = newId();
          setDoc(doc(db, 'comments', cid), { id: cid, taskId: task.id, projectId: task.projectId || null, text: t, at: Date.now(), by: { uid: identity?.uid || null, name: identity?.name || identity?.email || 'Me' } });
        }} />
      </Section>
    </div>
  );
}

function Head({ onClose, project, onProject, onExpand }) {
  return (
    <div className={s.head}>
      {project ? <button className={s.proj} onClick={onProject}><span className="dot" style={{ background: project.color }} />{project.icon} {project.name}</button> : <span className="label">Task</span>}
      <span className="spacer" />
      {onExpand ? <button className="btn ghost sm icon" onClick={onExpand} title="Open the full view — photos, who worked on it, trips" aria-label="Expand"><Icon name="maximize-2" size={15} /></button> : null}
      <button className="btn ghost sm icon" onClick={onClose} title="Close (Esc)" aria-label="Close"><Icon name="x" /></button>
    </div>
  );
}

// Notes linked to this task — a card's notes on a board (boards B5): newest first, open one, or write one linked here.
function LinkedNotes({ task }) {
  const navigate = useNavigate();
  const { notes, addNote } = useNotes();
  const list = useMemo(() => notesForCard(notes || [], task.id), [notes, task.id]);
  const write = () => navigate(`/notes/all/${addNote({ title: task.title, links: [{ type: 'task', id: task.id }, ...(task.projectId ? [{ type: 'project', id: task.projectId }] : [])] })}`);
  return (
    <Section title={`Notes${list.length ? ` · ${list.length}` : ''}`}>
      {list.slice(0, 6).map((n) => (
        <button key={n.id} className={s.sub} style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }} onClick={() => navigate(`/notes/all/${n.id}`)}>
          <Icon name="file-text" size={13} /><span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title || 'Untitled note'}</span>
          <span className="faint" style={{ fontSize: 11.5 }}>{n.date || ''}</span>
        </button>
      ))}
      <button className="btn sm ghost" onClick={write}><Icon name="edit-3" size={13} />Write a note on this task</button>
    </Section>
  );
}

function Section({ title, children }) {
  return <section className={s.section}><div className="label" style={{ marginBottom: 8 }}>{title}</div>{children}</section>;
}

export function AddLine({ placeholder, onAdd, icon = 'plus', autoFocus = false }) {
  const [v, setV] = useState('');
  return (
    <div className={s.add}>
      <Icon name={icon} size={13} />
      <input value={v} placeholder={placeholder} autoFocus={autoFocus} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && v.trim()) { onAdd(v.trim()); setV(''); } }} />
    </div>
  );
}

function InlineText({ value, onSave, done }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return <input className={s.inline} data-done={done || undefined} value={v} onChange={(e) => setV(e.target.value)}
    onBlur={() => { const t = v.trim(); if (t && t !== value) onSave(t); else setV(value); }}
    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />;
}

// Scope — was this in the quote? (shared/scope.js) One click; click the lit one again to clear. Billing follows it.
export const SCOPE_VAR = { included: 'var(--c-text-secondary)', extra: 'var(--c-success)', missed: 'var(--c-danger)', overhead: 'var(--c-purple)' };
function ScopeSection({ task, tasks, project, planAt, onPick, onClientOk }) {
  const extra = useExtraRate(project);
  const { identity } = useSession();
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const isExtra = (task.scope || null) === 'extra' || effectiveScope({ ...task, scope: null }, { planAt, byId }) === 'extra' && !task.scope;
  const locked = !!task.scopeLocked;
  const unlock = () => commitWrites(commands.unlockScope({ tasks, now: Date.now }, { id: task.id }).writes);
  const auto = effectiveScope({ ...task, scope: null }, { planAt, byId });
  const sortMe = needsScope(task, { planAt, byId });
  return (
    <Section title="Scope">
      <div className="row" style={{ flexWrap: 'wrap' }}>
        {SCOPES.map((k) => {
          const on = task.scope === k;
          const implied = !task.scope && auto === k;
          return (
            <button key={k} className={`chip${on ? ' on' : ''}`} title={SCOPE_HINT[k]} aria-pressed={on} disabled={locked} onClick={() => onPick(on ? null : k)}
              style={on || implied ? { borderColor: SCOPE_VAR[k], color: SCOPE_VAR[k], opacity: implied ? 0.8 : 1 } : null}>
              {SCOPE_LABEL[k]}
            </button>
          );
        })}
        {locked ? <span className="faint" style={{ fontSize: 12 }}>Locked in the quote</span> : sortMe ? <span style={{ color: 'var(--c-warning)', fontSize: 12 }}>sort me: not in the plan when work started</span>
          : !task.scope && auto ? <span className="faint" style={{ fontSize: 12 }}>{SCOPE_LABEL[auto]} (automatic)</span> : null}
        {locked && identity?.role === 'admin' ? <button className="btn ghost sm" onClick={unlock}><Icon name="unlock" size={12} />Unlock</button> : null}
      </div>
      {isExtra ? (
        <div className="row" style={{ flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
          <span className="num">{extra.rate ? `${moneyShort(extra.rate)}/h · ${extra.from === 'role' ? 'your rate' : 'project rate'}${task.timeSpent ? ` · ${moneyShort(Math.round((task.timeSpent / 3600000) * extra.rate))}` : ''}` : 'No rate set'}</span>
          {task.extraBilled ? <span className="faint">Billed</span> : (
            <>
              <span className="muted" style={{ marginLeft: 8 }}>Client OK</span>
              <button className={`chip${task.clientOk === true ? ' on' : ''}`} aria-pressed={task.clientOk === true} onClick={() => onClientOk(task.clientOk === true ? null : true)}>Yes</button>
              <button className={`chip${task.clientOk === false ? ' on' : ''}`} aria-pressed={task.clientOk === false} onClick={() => onClientOk(task.clientOk === false ? null : false)}>Not yet</button>
            </>
          )}
        </div>
      ) : null}
    </Section>
  );
}

/**
 * Tag chips + "+ tag" box. Suggests the suite's tag list (opsTags, from the books) first, then `vocab` (tags used
 * here; defaults to the tasks'). A merged tag shows as its target (shared/tags resolveTag).
 */
export function TagsRow({ tags: given, onChange, vocab: local }) {
  const navigate = useNavigate();
  const { tasks } = useTasks();
  const used = useMemo(() => local || tagVocab(tasks || []), [local, tasks]);
  const { vocab, shown: tags } = useTagPicker(given, used);
  const [v, setV] = useState('');
  const [focused, setFocused] = useState(false);
  const add = (t) => { const next = withTag(tags, t); if (next !== tags) onChange(next); setV(''); };
  const sugg = focused || v ? suggestTags(vocab, tags, v, 8) : [];
  return (
    <div style={{ marginTop: 8 }}>
      <div className="row" style={{ flexWrap: 'wrap' }}>
        {tags.map((t) => (
          // The name opens the tag's page (everything carrying it); the × drops it here.
          <span key={t} className="chip">
            <button onClick={() => navigate(tagPath(t))} title={`Everything tagged #${t}`}>#{t}</button>
            <button onClick={() => onChange(tags.filter((x) => x !== t))} title="Remove tag" aria-label={`Remove tag ${t}`}><Icon name="x" size={10} /></button>
          </span>
        ))}
        <input className={s.tagInput} value={v} placeholder="+ tag" onChange={(e) => setV(e.target.value)}
          onFocus={() => setFocused(true)} onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ',') && v.trim()) { e.preventDefault(); add(v); } }} />
      </div>
      {sugg.length ? (
        <div className="row" style={{ flexWrap: 'wrap', marginTop: 6, gap: 4 }}>
          {sugg.map((t) => <button key={t} className="chip" style={{ borderStyle: 'dashed' }} onMouseDown={(e) => e.preventDefault()} onClick={() => add(t)}>#{t}</button>)}
        </div>
      ) : null}
    </div>
  );
}

function EntryForm({ today, onAdd }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(today);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  if (!open) return <button className="btn ghost sm" onClick={() => setOpen(true)}><Icon name="plus" size={13} />Add time</button>;
  return (
    <div className={s.entryForm}>
      <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      <input className="input" type="time" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
      <input className="input" type="time" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
      <input className="input" value={note} placeholder="Note (optional)" onChange={(e) => setNote(e.target.value)} style={{ gridColumn: '1 / -1' }} />
      <div className="row" style={{ gridColumn: '1 / -1' }}>
        <button className="btn primary sm" disabled={!from || !to} onClick={() => { onAdd({ date, startTime: from, endTime: to, note: note || null }); setFrom(''); setTo(''); setNote(''); setOpen(false); }}>Add</button>
        <button className="btn ghost sm" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}
